import { groupBy, each, camelCase, sortBy, cloneDeep, reduce } from 'lodash';
import { ADD_OUTCOME, ADD_OUTCOME_FORECAST } from './PerformanceActions';

// Initial State
const initialState = {
  outcome: [],
  saleReps: [],
  status: null,
  errors: null,
};


function makeRepOutcomeOpportunities(opportunities, ownerDetails) {
  const owners = groupBy(opportunities, 'owner_id');
  const results = [];
  const stageStats = {
    total: 0,
    amount: 0,
    countPercentage: 0,
    amountPercentage: 0,
    hasData: false,
    pursuitLength: 0,
    pursuitCount: 0,
    avgPursuitTime: 0,
    avgDealSize: 0,
    daysPushed: 0,
    avgDaysPushed: 0,
    pushFrequency: 0,
    avgPushFrequency: 0,
    totalLargerThanZeroAmount: 0,
  };
  each(owners, (deals, ownerId) => {
    const result = {
      total: deals.length,
      amount: 0,
      owner_id: ownerId,
      user_id: ownerId,
      owner: ownerDetails[ownerId] || null,
      items: [],
      avgDealSize: 0,
      totalLargerThanZeroAmount: 0,
      pursuitLength: 0,
      pursuitCount: 0,
      avgPursuitTime: 0,
      daysPushed: 0,
      avgDaysPushed: 0,
      pushFrequency: 0,
      avgPushFrequency: 0,
      stages: {
        won: cloneDeep(stageStats),
        lost: cloneDeep(stageStats),
        noDecision: cloneDeep(stageStats),
      },
    };
    each(deals, (deal) => {
      const stage = camelCase(deal.stage);
      if (result.stages[stage]) {
        result.stages[stage].hasData = true;
        result.stages[stage].total += 1;
        result.stages[stage].amount += deal.amount || 0;
        if (deal.pursuit_length > 0 && deal.amount > 0) {
          // https://github.com/lumen-software/TruSales/issues/1202
          result.stages[stage].pursuitLength += deal.pursuit_length;
          result.stages[stage].pursuitCount += 1;
          result.pursuitLength += deal.pursuit_length;
          result.pursuitCount += 1;
        }
        if (deal.amount > 0) {
          result.stages[stage].totalLargerThanZeroAmount += 1;
          result.stages[stage].daysPushed += deal.pushed_days || 0;
          result.stages[stage].pushFrequency += deal.pushed_frequency || 0;
        }
      }
      result.amount += deal.amount || 0;
      result.items.push(deal);
      if (deal.amount > 0) {
        // https://github.com/lumen-software/TruSales/issues/1202
        result.totalLargerThanZeroAmount += 1;
        result.daysPushed += deal.pushed_days || 0;
        result.pushFrequency += deal.pushed_frequency || 0;
      }
    });

    each(result.stages, (values) => {
      values.countPercentage = result.total ? (values.total / result.total) * 100.00 : 0;
      values.amountPercentage = result.amount ? (values.amount / result.amount) * 100.00 : 0;
      values.avgPursuitTime = values.pursuitCount ? (values.pursuitLength / values.pursuitCount) : 0;
      values.avgDealSize = values.totalLargerThanZeroAmount ? (values.amount / values.totalLargerThanZeroAmount) : 0;
      values.avgDaysPushed = values.totalLargerThanZeroAmount ? (values.daysPushed / values.totalLargerThanZeroAmount) : 0;
      values.avgPushFrequency = values.totalLargerThanZeroAmount ? (values.pushFrequency / values.totalLargerThanZeroAmount) : 0;
    });
    result.avgDealSize = result.totalLargerThanZeroAmount ? (result.amount / result.totalLargerThanZeroAmount) : 0;
    result.avgPursuitTime = result.pursuitCount ? (result.pursuitLength / result.pursuitCount) : 0;
    result.avgDaysPushed = result.totalLargerThanZeroAmount ? (result.daysPushed / result.totalLargerThanZeroAmount) : 0;
    result.avgPushFrequency = result.totalLargerThanZeroAmount ? (result.pushFrequency / result.totalLargerThanZeroAmount) : 0;
    results.push(result);
  });
  return results;
}

function makeSaleReps(opportunities, ownerDetails) {
  const owners = groupBy(opportunities, 'owner_id');
  const results = [];
  each(owners, (deals, ownerId) => {
    const owner = ownerDetails[ownerId] || {};
    if (owner.is_active) {
      const result = {
        owner,
        user_id: owner ? owner.user_id : null,
        items: [],
      };
      each(deals, (deal) => {
        const { stage } = deal;
        result.items.push({ ...deal, types: deal.types, stage });
      });
      if (result.items.length >= 3) {
        // issues/251
        // at least 3 closed deals
        results.push(result);
      }
    }
  });
  return results;
}

const PerformanceReducer = (state = initialState, action) => {
  switch (action.type) {
    case ADD_OUTCOME: {
      const opportunities = sortBy(action.opportunities, 'amount');
      const indexedOwners = action.owners || {};
      each(opportunities, (opportunity) => {
        opportunity.owner = indexedOwners[opportunity.owner_id];
      });
      return {
        ...state,
        saleReps: makeSaleReps(opportunities, indexedOwners),
        outcome: makeRepOutcomeOpportunities(opportunities, indexedOwners),
        status: action.status,
        errors: action.errors,
      };
    }
    case ADD_OUTCOME_FORECAST: {
      const { outcome, saleReps } = state;
      const { status, forecasts, key } = action;
      const newOutcome = reduce(outcome, (items, o) => {
        const nOut = { ...o };
        nOut[`$${key}Status`] = status;
        if (status === 'loaded') {
          if (nOut.owner && nOut.owner.user_id && forecasts[nOut.owner.user_id] !== undefined) {
            nOut[`${key}Forecast`] = forecasts[nOut.owner.user_id];
          } else {
            nOut[`${key}Forecast`] = null;
          }
        } else if (status === 'error') {
          nOut[`${key}Forecast`] = null;
        }
        return items.concat(nOut);
      }, []);
      let newSalesReps = saleReps;
      newSalesReps = reduce(saleReps, (items, o) => {
        const nOut = { ...o };
        nOut[`$${key}Status`] = status;
        if (status === 'loaded') {
          if (nOut.owner && nOut.owner.user_id && forecasts[nOut.owner.user_id] !== undefined) {
            nOut[`${key}Forecast`] = forecasts[nOut.owner.user_id];
          } else {
            nOut[`${key}Forecast`] = null;
          }
        } else if (status === 'error') {
          nOut[`${key}Forecast`] = null;
        }
        return items.concat(nOut);
      }, []);
      return { ...state, outcome: newOutcome, saleReps: newSalesReps };
    }
    default:
      return state;
  }
};

/* Selectors */

// Get all posts
export const getOutcome = state => state.performance.outcome;
export const getSaleReps = state => state.performance.saleReps;
export const getLoading = state => state.performance.status === 'loading';
export const getLoaded = state => state.performance.status === 'loaded';
export const getHasErrors = state => state.performance.status === 'error';
export const getErrors = (state) => {
  if (state.performance.status === 'error') {
    return state.performance.errors;
  }
  return null;
};

// Export Reducer
export default PerformanceReducer;
